/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Linking,
} from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useFocusEffect } from '@react-navigation/native';
import { useHeaderHeight } from '@react-navigation/elements';

import * as Layout from '@components/screen/Layout';
import SociollaText from '@components/sociolla/SociollaText';
import Button from '@components/form/Button';
import ItemDetail from '@components/global/ItemDetail';
import TitleSubSection from '@components/global/TitleSubSection';
import Image from '@components/global/Image';
import StatusBadge from '@components/global/StatusBadge';
import ModalCancel from '@components/modal/ModalCancel';
import AttachmentsInput from '@components/form/AttachmentsInput';
import Input from '@components/form/Input';
import DatePicker from '@components/form/DatePicker';
import CheckBox from '@components/form/CheckBox';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import MoreButton from '@components/navbar/MoreButton';
import ModalLog from '@components/modal/ModalLog';
import RenderIf from '@components/global/RenderIf';
import FloatCommentButton from '@components/comment/FloatCommentButton';
import CommentHeaderButton from '@components/comment/CommentHeaderButton';
import CommentDrawer from '@components/comment/CommentDrawer';

import Colors from '@global/colors';
import { isWeb, isNative, isMobileWeb } from '@global/screens';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import Toast from 'react-native-toast-message';
import { REQUEST_PRODUCT_TESTER_LOG_FORMATTER } from '@/constants/log';
import { BASE_ROLES, COMPLETION_ROLES, SHIPPING_DATE_ROLES } from './constants';
import { exportPdf } from '@global/helpers';
import { translate } from '@global/translate';

const defaultUserImg = require('@/assets/img/default/user-image.png');

// Layout.ButtonSticky overlays the content: each row is a 36px button + 15px margin.
const STICKY_BUTTON_SPACE = 51;

const ContentWrapper = ({ bottomSpace = 0, children }) => {
  const headerHeight = useHeaderHeight();
  if (isWeb) {
    return (
      <Layout.Wrapper rounded hasProgressBar>
        {children}
      </Layout.Wrapper>
    );
  }
  // The mobile-web stack card renders in page mode (auto height), so bound the
  // ScrollView to the viewport or it grows to its content and never scrolls.
  return (
    <ScrollView
      keyboardShouldPersistTaps="handled"
      style={
        isMobileWeb ? { height: `calc(100dvh - ${headerHeight}px)` } : undefined
      }
      contentContainerStyle={{
        flexGrow: 1,
        paddingBottom: 50 + bottomSpace,
      }}>
      <Layout.Wrapper rounded hasProgressBar minHeight={1} isUndefined>
        {children}
      </Layout.Wrapper>
    </ScrollView>
  );
};

const RequestProductDetailScreen = ({ navigation, route, ...props }) => {
  const { id, prev_page, prev_id } = route.params || {};
  const didFetchRef = useRef(false);
  const [activeTab, setActiveTab] = useState('details');
  const [detailData, setDetailData] = useState(null);
  const [showComments, setShowComments] = useState(
    route.params?.openComments === 'true',
  );
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [modalLog, setModalLog] = useState(false);
  const [showAllApprovals, setShowAllApprovals] = useState(false);
  const [productSkip, setProductSkip] = useState(0);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [loadingMoreProducts, setLoadingMoreProducts] = useState(false);
  const [receivedDate, setReceivedDate] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [deliveryOrder, setDeliveryOrder] = useState('');
  const [productStatus, setProductStatus] = useState({});
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isMarkAll, setIsMarkAll] = useState(false);

  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const getDetail = useApi(MsOrder.getRequestProductTesterDetail);
  const getProducts = useApi(MsOrder.getProductsFromRequestProductTester);

  const completionRoles = COMPLETION_ROLES;
  const canPerformCompletionAction = completionRoles.includes(user?.role?.code);

  const from = route?.params?.name;

  useFocusEffect(
    useCallback(() => {
      if (!didFetchRef.current) {
        didFetchRef.current = true;
        if (id) {
          fetchData();
        }
      }

      return () => {
        didFetchRef.current = false;
        resetData();
      };
    }, [route]),
  );

  useEffect(() => {
    if (isWeb) {
      return;
    }
    const status = detailData?.status;
    if (!status) {
      return;
    }

    const baseRoles = BASE_ROLES;
    const canPerformBaseAction = baseRoles.includes(user?.role?.code);
    const menus = [];

    if (status == 'draft') {
      if (canPerformBaseAction) {
        menus.push({
          type: 'function',
          name: 'Edit',
          callback: () => handleAction('edit'),
        });
        menus.push({
          type: 'function',
          name: 'Delete',
          callback: () => setShowDeleteModal(true),
        });
        menus.push({
          type: 'function',
          name: 'Request Confirmation',
          callback: () =>
            confirmAction(
              'request_confirmation',
              'Request Confirmation',
              'Are you sure you want to request confirmation?',
            ),
        });
      }
    } else if (status == 'need_approval') {
      const isCreator = detailData?.user?.id === user?._id;

      if (isCreator) {
        menus.push({
          type: 'function',
          name: 'Cancel',
          callback: () => setShowCancelModal(true),
        });
      }
    } else if (status == 'rejected') {
      menus.push({
        type: 'function',
        name: 'Edit',
        callback: () => handleAction('edit'),
      });
    }

    navigation.setOptions({
      headerRight: () => {
        return isWeb ? (
          <NotificationSettingButton {...props} />
        ) : (
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {detailData?._id ? (
              <CommentHeaderButton
                onPress={() => setShowComments(true)}
                drawerVisible={showComments}
                entityType="request_product"
                entityId={detailData._id}
              />
            ) : (
              false
            )}
            {menus.length ? <MoreButton menu={menus} {...props} /> : false}
          </View>
        );
      },
    });
  }, [detailData, navigation, user, showComments]);

  const formatProduct = (prods, isMark) => {
    return prods.map((p) => ({
      ...p,
      is_received: isMark || p.is_received,
    }));
  };

  const resetData = () => {
    setActiveTab('details');
    setDetailData(null);
    setProducts([]);
    setShowAllApprovals(false);
    setReceivedDate(null);
    setAttachments([]);
    setDeliveryNotes('');
    setProductStatus({});
    setIsMarkAll(false);
  };

  const fetchData = async () => {
    setLoading(true);
    setProductSkip(0);
    setHasMoreProducts(true);
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const [detailRes, productsRes] = await Promise.all([
        getDetail.request(id),
        getProducts.request(id, { params: { limit: 10, skip: 0 } }),
      ]);

      if (detailRes?.data?.data) {
        setDetailData(detailRes.data.data);
        const data = detailRes.data.data;
        if (data.received_date) {
          setReceivedDate(new Date(data.received_date));
        }
        if (data.attachments && data.attachments.length > 0) {
          setAttachments(
            data.attachments.map((url, index) => ({
              name: `Attachment ${index + 1}`,
              url: url,
            })),
          );
        } else if (data.attachment) {
          setAttachments([{ name: 'Attachment 1', url: data.attachment }]);
        }
        if (data.notes) {
          setDeliveryNotes(data.notes);
        }
        if (data.delivery_order) {
          setDeliveryOrder(data.delivery_order);
        }
        setIsMarkAll(data.is_all_received);

        if (productsRes?.data?.data) {
          setProducts(
            formatProduct(productsRes.data.data, data.is_all_received),
          );
          if (productsRes.data.data.length < 10) {
            setHasMoreProducts(false);
          }
        }
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong';
      Toast.show({
        type: 'error',
        text1: errorMessage,
      });
    } finally {
      setLoading(false);
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  useEffect(() => {
    if (products.length > 0) {
      setProductStatus((prev) => {
        const next = { ...prev };
        products.forEach((p) => {
          const id = p._id || p.id;
          if (next[id] === undefined) {
            next[id] = p.is_received || false;
          }
        });
        return next;
      });
    }
  }, [products]);

  const buildCompletionProductsPayload = async () => {
    const pageSize = 10;
    let skip = 0;
    let hasMore = true;
    const allProducts = [];

    while (hasMore) {
      const response = await MsOrder.getProductsFromRequestProductTester(id, {
        params: { limit: pageSize, skip },
      });
      const batch = response?.data?.data || [];
      allProducts.push(...batch);
      hasMore = batch.length === pageSize;
      skip += pageSize;
    }

    return allProducts.map((product) => {
      const productId = product._id || product.id;
      return {
        id: productId,
        is_received:
          productStatus[productId] !== undefined
            ? productStatus[productId]
            : isMarkAll || product.is_received || false,
      };
    });
  };

  const handleAction = async (action, note = '') => {
    try {
      setLoading(true);
      dispatch(showPopup({ name: 'isLoading', value: true }));

      if (action === 'delete') {
        await MsOrder.deleteRequestProductTester(id, { reason: note });

        Toast.show({
          type: 'success',
          text1: 'Request deleted successfully',
        });
        setTimeout(() => {
          navigation.navigate('RequestProductListScreen');
        }, 1000);
      } else if (action === 'edit') {
        navigation.navigate('RequestProductFormScreen', {
          id,
          reference_code: detailData?.reference_code,
          name: detailData?.name,
        });
        setLoading(false);
        dispatch(showPopup({ name: 'isLoading', value: false }));
        return;
      } else if (action === 'complete') {
        const products = await buildCompletionProductsPayload();
        const payload = {
          status: 'completed',
          shipping_date: detailData?.shipping_date,
          received_date: receivedDate ? receivedDate.toISOString() : null,
          attachments: attachments.map((item) => item.url),
          notes: deliveryNotes,
          products,
          is_all_received: isMarkAll,
        };
        await MsOrder.updateRequestProductDeliveryConfirmation(id, payload);

        Toast.show({
          type: 'success',
          text1: 'Request completed successfully',
        });
        setTimeout(() => {
          fetchData();
        }, 500);
      } else {
        let status = '';
        if (action === 'request_confirmation') {
          status = 'need_approval';
        } else if (action === 'confirm') {
          status = 'approved';
        } else if (action === 'reject') {
          status = 'rejected';
        } else if (action === 'cancel') {
          status = 'cancelled';
        }

        const payload = { reason: note };
        await MsOrder.updateRequestProductStatus(id, status, payload);

        Toast.show({
          type: 'success',
          text1: 'Request updated successfully',
        });
        fetchData();
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong';
      Toast.show({
        type: 'error',
        text1: errorMessage,
      });
    } finally {
      setLoading(false);
      dispatch(showPopup({ name: 'isLoading', value: false }));
      if (action === 'reject') {
        setShowRejectModal(false);
      }
      if (action === 'delete') {
        setShowDeleteModal(false);
      }
      if (action === 'cancel') {
        setShowCancelModal(false);
      }
    }
  };

  const handleUpdateShippingDate = async (date) => {
    try {
      setLoading(true);
      dispatch(showPopup({ name: 'isLoading', value: true }));

      const payload = {
        status: 'approved',
        shipping_date: date.toISOString(),
      };
      await MsOrder.updateRequestProductDeliveryConfirmation(id, payload);

      Toast.show({
        type: 'success',
        text1: 'Shipping Date updated successfully',
      });
      fetchData();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong';
      Toast.show({
        type: 'error',
        text1: errorMessage,
      });
    } finally {
      setLoading(false);
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const handleUpdateDeliveryOrder = async (value) => {
    if ((value || '') === (detailData?.delivery_order || '')) {
      return;
    }
    try {
      setLoading(true);
      dispatch(showPopup({ name: 'isLoading', value: true }));

      const payload = {
        status: 'approved',
        delivery_order: value,
      };
      await MsOrder.updateRequestProductDeliveryConfirmation(id, payload);

      Toast.show({
        type: 'success',
        text1: 'Delivery Order updated successfully',
      });
      fetchData();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong';
      Toast.show({
        type: 'error',
        text1: errorMessage,
      });
    } finally {
      setLoading(false);
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const loadMoreProducts = async () => {
    if (loadingMoreProducts || !hasMoreProducts) {
      return;
    }

    setLoadingMoreProducts(true);
    try {
      const nextSkip = productSkip + 10;
      const params = {
        limit: 10,
        skip: nextSkip,
      };
      const response = await getProducts.request(id, { params });

      if (response?.data?.data) {
        const newProducts = response.data.data;
        const updated = [...products, ...newProducts];
        setProducts(formatProduct(updated, isMarkAll));
        setProductSkip(nextSkip);

        if (newProducts.length < 10) {
          setHasMoreProducts(false);
        }
      } else {
        setHasMoreProducts(false);
      }
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to load more products',
      });
    } finally {
      setLoadingMoreProducts(false);
    }
  };

  const handleDeliveryAction = async (type) => {
    try {
      setLoading(true);
      dispatch(showPopup({ name: 'isLoading', value: true }));

      const payload = {
        status: type === 'complete' ? 'completed' : 'approved',
        shipping_date: detailData?.shipping_date,
        received_date: receivedDate ? receivedDate.toISOString() : null,
        attachments: attachments.map((item) => item.url),
        notes: deliveryNotes,
        products: Object.keys(productStatus).map((id) => ({
          id,
          is_received: productStatus[id],
        })),
        is_all_received: isMarkAll,
      };

      await MsOrder.updateRequestProductDeliveryConfirmation(id, payload);

      Toast.show({
        type: 'success',
        text1:
          type === 'complete'
            ? 'Request completed successfully'
            : 'Delivery confirmation saved successfully',
      });
      fetchData();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong';
      Toast.show({
        type: 'error',
        text1: errorMessage,
      });
    } finally {
      setLoading(false);
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const confirmAction = (action, title, message) => {
    dispatch(
      showAlert({
        show: true,
        title: title,
        description: message,
        isConfirm: true,
        onCallback: () => handleAction(action),
      }),
    );
  };

  const renderActions = () => {
    if (!isWeb) {
      return null;
    }
    const status = detailData?.status;
    if (!status) {
      return null;
    }

    const buttons = [];
    const baseRoles = BASE_ROLES;
    const completionRoles = COMPLETION_ROLES;

    const canPerformBaseAction = baseRoles.includes(user?.role?.code);
    const canPerformCompletionAction = completionRoles.includes(
      user?.role?.code,
    );

    if (status === 'draft') {
      if (canPerformBaseAction) {
        buttons.push(
          <Button
            key="edit"
            type="outline"
            title="Edit"
            buttonStyle={{ marginRight: 10, width: 80 }}
            onPress={() => handleAction('edit')}
          />,
        );
        buttons.push(
          <Button
            key="delete"
            type="primary"
            title="Delete"
            buttonStyle={{ marginRight: 10, width: 80 }}
            onPress={() => setShowDeleteModal(true)}
          />,
        );
        buttons.push(
          <Button
            key="request_confirmation"
            type="primary"
            title="Request Confirmation"
            buttonStyle={{ width: 180 }}
            onPress={() =>
              confirmAction(
                'request_confirmation',
                'Request Confirmation',
                'Are you sure you want to request confirmation?',
              )
            }
          />,
        );
      }
    } else if (status === 'need_approval') {
      const isWaitingApproval = detailData?.waiting_approvals?.some(
        (approver) => approver.id === user?._id,
      );
      const isCurrentRole = detailData?.current_approval === user?.role?.code;

      const isCreator = detailData?.user?.id === user?._id;

      if (isCreator) {
        buttons.push(
          <Button
            key="cancel"
            type="outline"
            title="Cancel"
            buttonStyle={{ marginRight: 10, width: 80 }}
            onPress={() => setShowCancelModal(true)}
          />,
        );
      }

      if (isWaitingApproval && isCurrentRole) {
        buttons.push(
          <Button
            key="reject"
            type="outline"
            title="Reject"
            buttonStyle={{ marginRight: 10, width: 100 }}
            onPress={() => setShowRejectModal(true)}
          />,
        );
        buttons.push(
          <Button
            key="confirm"
            type="primary"
            title="Confirm"
            buttonStyle={{ width: 100 }}
            onPress={() =>
              confirmAction(
                'confirm',
                'Confirm Request',
                'Are you sure you want to confirm this request?',
              )
            }
          />,
        );
      }
    } else if (status === 'approved') {
      const isCompleteEnabled =
        receivedDate &&
        attachments.length > 0 &&
        Object.keys(productStatus).length > 0;
      if (canPerformCompletionAction) {
        buttons.push(
          <Button
            key="save"
            type="outline"
            title="Save"
            buttonStyle={{ marginRight: 10, width: 80 }}
            onPress={() => handleDeliveryAction('save')}
          />,
        );
        buttons.push(
          <Button
            key="complete"
            type="primary"
            title="Complete"
            disabled={!isCompleteEnabled}
            buttonStyle={{ minWidth: 100 }}
            onPress={() =>
              confirmAction(
                'complete',
                'Complete Request',
                'Are you sure you want to complete this request?',
              )
            }
          />,
        );
      }
    } else if (status === 'rejected') {
      if (canPerformBaseAction) {
        buttons.push(
          <Button
            key="edit"
            type="outline"
            title="Edit"
            buttonStyle={{ marginRight: 10, width: 80 }}
            onPress={() => handleAction('edit')}
          />,
        );
      }
    }

    buttons.unshift(
      <Button
        key="export_pdf"
        type="primary"
        rounded
        buttonStyle={{ width: 42, marginRight: 10 }}
        title={
          <Image
            style={{ width: 13, height: 17 }}
            width={13}
            height={17}
            source={require('@assets/img/icons/pdf.png')}
          />
        }
        onPress={() => exportPdf('request-product', id)}
      />,
    );

    return <View style={{ flexDirection: 'row' }}>{buttons}</View>;
  };

  const getAddressString = (addressObj) => {
    if (!addressObj) {
      return '-';
    }

    if (typeof addressObj === 'string') {
      return addressObj;
    }

    const parts = [];
    if (addressObj.street) {
      parts.push(addressObj.street);
    }
    if (addressObj.city) {
      parts.push(
        typeof addressObj.city === 'object'
          ? addressObj.city.name
          : addressObj.city,
      );
    }
    if (addressObj.province) {
      parts.push(
        typeof addressObj.province === 'object'
          ? addressObj.province.name
          : addressObj.province,
      );
    }
    if (addressObj.state) {
      parts.push(
        typeof addressObj.state === 'object'
          ? addressObj.state.name
          : addressObj.state,
      );
    }
    if (addressObj.country) {
      parts.push(
        typeof addressObj.country === 'object'
          ? addressObj.country.name
          : addressObj.country,
      );
    }
    if (addressObj.zip_code) {
      parts.push(addressObj.zip_code);
    }

    return parts.length > 0 ? parts.join(', ') : addressObj.name || '-';
  };

  const getNotes = () => {
    if (detailData?.status == 'rejected') {
      return detailData?.rejected_reason;
    } else if (detailData?.status == 'cancelled') {
      return detailData?.cancelled_reason;
    }
    return '-';
  };

  const renderTabButton = (key, label) => (
    <TouchableOpacity
      style={[styles.tabButton, activeTab === key && styles.activeTabButton]}
      onPress={() => setActiveTab(key)}>
      <SociollaText
        type={activeTab === key ? 'bold' : 'regular'}
        style={[styles.tabText, activeTab === key && styles.activeTabText]}>
        {label}
      </SociollaText>
    </TouchableOpacity>
  );

  const renderUser = (name, image) => (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <Image
        style={{ width: 20, height: 20, marginRight: 8 }}
        width={20}
        height={20}
        isResolveAsset
        source={image ? { uri: image } : defaultUserImg}
      />
      <SociollaText
        style={{
          fontSize: 12,
          color: Colors.blue3,
          alignSelf: 'center',
        }}>
        {name || '-'}
      </SociollaText>
    </View>
  );

  const generalInfo = [
    {
      name: 'Request Number',
      valueComponent: (
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <SociollaText style={[styles.value, { marginRight: 10 }]}>
            {detailData?.reference_code || detailData?.code}
          </SociollaText>
        </View>
      ),
    },
    {
      name: 'Status',
      valueComponent: (
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <StatusBadge status={detailData?.status} />
          <TouchableOpacity
            onPress={() => setModalLog(true)}
            style={{ marginLeft: 5 }}>
            <SociollaText style={{ color: Colors.blue0, fontSize: 12 }}>
              see log
            </SociollaText>
          </TouchableOpacity>
        </View>
      ),
    },
    {
      name: 'Notes',
      value: getNotes(),
    },
    {
      name: 'Waiting Approval',
      valueComponent:
        detailData?.waiting_approvals?.length > 0 ? (
          <View>
            {(showAllApprovals
              ? detailData.waiting_approvals
              : detailData.waiting_approvals.slice(0, 3)
            ).map((approver, index) => (
              <View key={index} style={{ marginBottom: 4 }}>
                {renderUser(approver.name, approver.image)}
              </View>
            ))}
            {detailData.waiting_approvals.length > 3 && (
              <TouchableOpacity
                onPress={() => setShowAllApprovals(!showAllApprovals)}>
                <SociollaText
                  style={{ color: Colors.blue10, fontSize: 12, marginTop: 4 }}>
                  {showAllApprovals ? 'See Less' : 'See More'}
                </SociollaText>
              </TouchableOpacity>
            )}
          </View>
        ) : (
          <SociollaText style={styles.value}>-</SociollaText>
        ),
      isHide: detailData?.status != 'need_approval',
    },
    {
      name: 'Request Date',
      value: detailData?.created_at
        ? moment(detailData.created_at).format('DD MMM YYYY, HH:mm')
        : '-',
    },
    {
      name: 'Request by',
      valueComponent: renderUser(
        detailData?.user?.name,
        detailData?.user?.image,
      ),
    },
  ];

  const customerDetail = [
    {
      name: 'Customer',
      valueStyle: { color: Colors.blue0 },
      value: detailData?.customer?.name || '-',
      onPress: () => {
        navigation.navigate('CustomerNavigator', {
          screen: 'CustomerDetailScreen',
          params: {
            id: detailData.customer?.id,
            rpt_id: detailData._id,
            name: detailData.customer?.name,
            prev_page: 'rpt',
            reference_code: detailData.reference_code,
          },
        });
      },
    },
    { name: 'Customer Code', value: detailData?.customer?.code || '-' },
    { name: 'Outlet Name', value: detailData?.outlet?.name || '-' },
    { name: 'Outlet Code', value: detailData?.outlet?.code || '-' },
    {
      name: 'Market Type',
      value: detailData?.customer?.market_type?.code?.toUpperCase() || '-',
    },
    {
      name: 'Business Type',
      value: detailData?.customer?.market_type?.business_type?.code || '-',
    },
    {
      name: 'Sales Team',
      value: detailData?.customer?.sales_team?.name || '-',
    },
    { name: 'Delivery Address', value: getAddressString(detailData?.address) },
  ];

  const renderAttachments = (attachmentsList) => {
    const completionRoles = COMPLETION_ROLES;
    const canPerformCompletionAction = completionRoles.includes(
      user?.role?.code,
    );

    if (detailData?.status === 'approved' && canPerformCompletionAction) {
      return (
        <AttachmentsInput
          showLabel={true}
          label=""
          value={attachments}
          onChange={(updatedAttachments) => setAttachments(updatedAttachments)}
        />
      );
    }

    if (!attachmentsList || attachmentsList.length === 0) {
      return <SociollaText style={styles.value}>-</SociollaText>;
    }

    return (
      <View>
        {attachmentsList.map((url, index) => {
          const isImage = url.match(/\.(jpeg|jpg|gif|png)$/) != null;
          return (
            <TouchableOpacity key={index} onPress={() => Linking.openURL(url)}>
              {isImage ? (
                <Image
                  source={{ uri: url }}
                  style={{
                    width: 100,
                    height: 100,
                    borderRadius: 8,
                    marginTop: 5,
                    marginBottom: 5,
                  }}
                  width={100}
                  height={100}
                />
              ) : (
                <SociollaText
                  style={[
                    styles.value,
                    { color: Colors.blue10, marginBottom: 5 },
                  ]}
                  numberOfLines={1}>
                  Attachment {index + 1}
                </SociollaText>
              )}
            </TouchableOpacity>
          );
        })}
      </View>
    );
  };

  const deliveryInfo = [
    {
      name: 'Shipping Date',
      valueComponent: (() => {
        const allowedRoles = SHIPPING_DATE_ROLES;
        const canEdit = allowedRoles.includes(user?.role?.code);

        return detailData?.status === 'approved' && canEdit ? (
          <View style={{ width: 200 }}>
            <DatePicker
              value={
                detailData?.shipping_date
                  ? new Date(detailData.shipping_date)
                  : null
              }
              onChange={(date) => handleUpdateShippingDate(date)}
              placeholder="Select Shipping Date"
              style={{ width: '100%' }}
            />
          </View>
        ) : (
          <SociollaText style={styles.value}>
            {detailData?.shipping_date
              ? moment(detailData.shipping_date).format('DD MMM YYYY')
              : '-'}
          </SociollaText>
        );
      })(),
    },
    {
      name: 'Delivery Order',
      valueComponent: (() => {
        const allowedRoles = SHIPPING_DATE_ROLES;
        const canEdit = allowedRoles.includes(user?.role?.code);

        return detailData?.status === 'approved' && canEdit ? (
          <View style={{ width: 200 }}>
            <Input
              placeholder="Input delivery order"
              value={deliveryOrder}
              onChangeText={setDeliveryOrder}
              onBlur={() => handleUpdateDeliveryOrder(deliveryOrder)}
              onEndEditing={() => handleUpdateDeliveryOrder(deliveryOrder)}
              inputContainerStyle={{
                borderWidth: 1,
                borderColor: Colors.grey4,
                paddingHorizontal: 10,
              }}
            />
          </View>
        ) : (
          <SociollaText style={styles.value}>
            {detailData?.delivery_order || '-'}
          </SociollaText>
        );
      })(),
    },
    {
      name: 'Recipient PIC',
      valueComponent: (
        <SociollaText style={styles.value}>
          {detailData?.recipient_pic || '-'}
        </SociollaText>
      ),
    },
    {
      name: 'Recipient Phone Number',
      valueComponent: (
        <SociollaText style={styles.value}>
          {detailData?.recipient_phone || '-'}
        </SociollaText>
      ),
    },
    {
      name: 'Received Date',
      valueComponent: (() => {
        const completionRoles = COMPLETION_ROLES;
        const canEdit = completionRoles.includes(user?.role?.code);

        return detailData?.status === 'approved' && canEdit ? (
          <View style={{ width: 200 }}>
            <DatePicker
              value={receivedDate}
              onChange={(date) => setReceivedDate(date)}
              placeholder={
                detailData?.shipping_date
                  ? 'Select Date'
                  : 'Fill Shipping Date first'
              }
              disabled={!detailData?.shipping_date}
              disableBeforeDate={detailData?.shipping_date}
              style={{ width: '100%' }}
            />
          </View>
        ) : (
          <SociollaText style={styles.value}>
            {detailData?.received_date
              ? moment(detailData.received_date).format('DD MMM YYYY')
              : '-'}
          </SociollaText>
        );
      })(),
    },
    {
      name: 'Attachment',
      valueComponent: renderAttachments(
        detailData?.attachments?.length > 0
          ? detailData.attachments
          : detailData?.attachment
          ? [detailData.attachment]
          : [],
      ),
    },
    {
      name: 'Notes',
      valueComponent: (() => {
        const completionRoles = COMPLETION_ROLES;
        const canEdit = completionRoles.includes(user?.role?.code);

        return detailData?.status === 'approved' && canEdit ? (
          <View style={{ width: 200 }}>
            <Input
              placeholder="Input notes"
              value={deliveryNotes}
              onChangeText={setDeliveryNotes}
              inputContainerStyle={{
                borderWidth: 1,
                borderColor: Colors.grey4,
                paddingHorizontal: 10,
              }}
            />
          </View>
        ) : (
          <SociollaText style={styles.value}>
            {detailData?.notes || '-'}
          </SociollaText>
        );
      })(),
    },
  ];

  if (loading && !detailData) {
    return <View style={{ flex: 1, backgroundColor: Colors.white }} />;
  }

  const getStickyButtons = () => {
    const status = detailData?.status;
    const buttons = [];

    if (activeTab === 'details') {
      if (status === 'need_approval') {
        const isWaitingApproval = detailData?.waiting_approvals?.some(
          (approver) => approver.id === user?._id,
        );
        const isCurrentRole = detailData?.current_approval === user?.role?.code;

        if (isWaitingApproval && isCurrentRole) {
          buttons.push(
            {
              title: 'Reject',
              type: 'default',
              onPress: () => {
                setShowRejectModal(true);
              },
            },
            {
              title: 'Confirm',
              onPress: () => {
                confirmAction(
                  'confirm',
                  'Confirm Request',
                  'Are you sure you want to confirm this request?',
                );
              },
            },
          );
        }
      }
    } else if (activeTab === 'delivery') {
      if (status === 'approved' && canPerformCompletionAction) {
        const isCompleteEnabled =
          receivedDate &&
          attachments.length > 0 &&
          Object.keys(productStatus).length > 0;

        buttons.push(
          // {
          //   title: 'Cancel',
          //   type: 'default',
          //   onPress: () => {
          //     navigation.goBack()
          //   },
          // },
          {
            title: 'Save',
            type: 'default',
            onPress: () => {
              handleDeliveryAction('save');
            },
          },
          {
            title: 'Complete',
            disabled: !isCompleteEnabled,
            onPress: () => {
              confirmAction(
                'complete',
                'Complete Request',
                'Are you sure you want to complete this request?',
              );
            },
          },
        );
      }
    }

    return buttons;
  };

  const renderProductItem = (item, index, withoutBorder = false) => {
    const combination = item.combination || {};
    const reference = combination.reference;
    const name = item.name;
    const brandName = item.brand?.name;
    const categoryName = item.category?.name;
    const imageUrl = combination.image;

    const attributes = combination.attributes || {};
    const attributeKeys = Object.keys(attributes);

    const handleCopy = (text, label) => {
      if (!text) {
        return;
      }
      if (isWeb) {
        navigator.clipboard.writeText(text);
      } else {
        Clipboard.setString(text);
      }
      Toast.show({
        type: 'success',
        text1: `${label} copied!`,
      });
    };

    return (
      <View
        key={index}
        style={[styles.productItem, withoutBorder && { borderBottomWidth: 0 }]}>
        <Image
          source={{ uri: imageUrl }}
          style={styles.productImage}
          width={60}
          height={60}
        />
        <View style={{ flex: 1, marginLeft: 15 }}>
          {reference ? (
            <View style={styles.rowCenter}>
              <SociollaText type="medium" style={styles.productReference}>
                {reference}
              </SociollaText>
              <TouchableOpacity
                onPress={() => handleCopy(reference, 'Reference')}>
                <Image
                  style={{ width: 14, height: 14, marginLeft: 6 }}
                  width={14}
                  height={14}
                  source={require('@/assets/img/icons/copy.svg')}
                  svg={true}
                />
              </TouchableOpacity>
            </View>
          ) : null}

          {name ? (
            <View style={[styles.rowCenter, { marginTop: 4 }]}>
              <SociollaText
                type="medium"
                style={styles.productName}
                numberOfLines={2}>
                {name}
              </SociollaText>
              <TouchableOpacity
                onPress={() => handleCopy(name, 'Product name')}>
                <Image
                  style={{ width: 14, height: 14, marginLeft: 6 }}
                  width={14}
                  height={14}
                  source={require('@/assets/img/icons/copy.svg')}
                  svg={true}
                />
              </TouchableOpacity>
            </View>
          ) : null}

          <SociollaText style={styles.productBrand}>{brandName}</SociollaText>

          {attributeKeys.map((key) => {
            const attr = attributes[key];
            const attrName = attr?.name || attr?.value || '';
            if (!attrName) {
              return null;
            }
            const label = key.charAt(0).toUpperCase() + key.slice(1);
            return (
              <SociollaText key={key} style={styles.productMeta}>
                {label}: {attrName}
              </SociollaText>
            );
          })}

          <SociollaText style={styles.productMeta}>{categoryName}</SociollaText>

          <SociollaText
            style={[styles.quantity, { marginTop: 2, color: '#7D838F' }]}>
            Qty: {item.quantity} pcs
          </SociollaText>
        </View>
      </View>
    );
  };

  const renderDeliveryProductItem = (item, index, withoutBorder = false) => {
    const combination = item.combination || {};
    const name = item.name;
    const brandName = item.brand?.name;
    const imageUrl = combination.image;
    const id = item._id || item.id;
    const isReceived = productStatus[id];

    return (
      <View
        key={index}
        style={[styles.productItem, withoutBorder && { borderBottomWidth: 0 }]}>
        <Image
          source={{ uri: imageUrl }}
          style={styles.productImage}
          width={60}
          height={60}
        />
        <View style={{ flex: 1, marginLeft: 15 }}>
          <SociollaText style={styles.productBrand}>{brandName}</SociollaText>
          {name ? (
            <SociollaText
              type="medium"
              style={styles.productName}
              numberOfLines={2}>
              {name}
            </SociollaText>
          ) : null}
          <SociollaText style={styles.productReference}>
            {combination.reference}
          </SociollaText>
          <SociollaText
            style={[styles.quantity, { marginTop: 2, color: '#7D838F' }]}>
            Qty: {item.quantity} pcs
          </SociollaText>

          <View style={{ flexDirection: 'row', marginTop: 10 }}>
            <TouchableOpacity
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginRight: 20,
              }}
              onPress={() => {
                setProductStatus((prev) => {
                  const next = { ...prev, [id]: true };
                  const allReceived = products.every(
                    (p) => next[p._id || p.id] === true,
                  );
                  setIsMarkAll(allReceived);
                  return next;
                });
              }}>
              <View
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 8,
                  borderWidth: 2,
                  borderColor: isReceived === true ? Colors.green1 : '#C5C5C5',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 8,
                }}>
                {isReceived === true && (
                  <View
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: Colors.green1,
                    }}
                  />
                )}
              </View>
              <SociollaText>Received</SociollaText>
            </TouchableOpacity>

            <TouchableOpacity
              style={{ flexDirection: 'row', alignItems: 'center' }}
              onPress={() => {
                setProductStatus((prev) => ({ ...prev, [id]: false }));
                setIsMarkAll(false);
              }}>
              <View
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 8,
                  borderWidth: 2,
                  borderColor: isReceived === false ? Colors.red3 : '#C5C5C5',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 8,
                }}>
                {isReceived === false && (
                  <View
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: Colors.red3,
                    }}
                  />
                )}
              </View>
              <SociollaText>Not Received</SociollaText>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  const renderModalLog = () => {
    return (
      modalLog && (
        <ModalLog
          show={modalLog}
          status={detailData?.status}
          data={detailData}
          onClose={() => setModalLog(false)}
          logType="request_product_tester"
          logId={id}
          logLabel="Request Product Tester"
          logFormatter={REQUEST_PRODUCT_TESTER_LOG_FORMATTER}
        />
      )
    );
  };

  const goBack = () => {
    if (from === 'Request Product Tester Approval') {
      navigation.navigate('TaskNotificationNavigator', {
        screen: 'TaskNotificationScreen',
        params: {
          task: true,
        },
      });
    } else if (prev_page == 'product-listing') {
      navigation.navigate('RequestProductNavigator', {
        screen: 'RequestProductListingDetailScreen',
        params: {
          titleScreen: detailData?.customer?.code,
          name: detailData?.customer?.code,
          id: prev_id,
          year: `${detailData?.year}`,
          go_back_key: route.key,
        },
      });
    } else {
      navigation.navigate('RequestProductListScreen');
    }
  };

  const stickyButtons = getStickyButtons();

  return (
    <Layout.Container>
      {isWeb ? (
        <Layout.Wrapper white={false}>
          <Layout.Back
            title={
              from === 'Request Product Tester Approval'
                ? 'Task'
                : 'Request Product List'
            }
            onCallback={goBack}
          />
        </Layout.Wrapper>
      ) : null}

      <ContentWrapper bottomSpace={stickyButtons.length * STICKY_BUTTON_SPACE}>
        {isWeb && (
          <View style={{ paddingHorizontal: 15, marginTop: 15 }}>
            <Layout.Title
              title={detailData?.reference_code || 'Request Product Detail'}>
              {renderActions()}
            </Layout.Title>
          </View>
        )}
        <View style={styles.tabContainer}>
          {renderTabButton('details', 'Details')}
          {renderTabButton('delivery', 'Delivery Confirmation')}
        </View>

        <View>
          <View>
            {activeTab === 'details' ? (
              <>
                <View style={[styles.section, styles.contentContainer]}>
                  {generalInfo
                    .filter((item) => !item.isHide)
                    .map((item, index) => (
                      <ItemDetail
                        key={index}
                        name={item.name}
                        value={item.value}
                        valueComponent={item.valueComponent}
                        withoutBorder={index === generalInfo.length - 1}
                      />
                    ))}
                </View>

                <TitleSubSection title="CUSTOMER DETAIL" />

                <View style={[styles.section, styles.contentContainer]}>
                  {customerDetail.map((item, index) => (
                    <ItemDetail
                      key={index}
                      name={item.name}
                      value={item.value}
                      valueComponent={item.valueComponent}
                      valueStyle={item.valueStyle}
                      onPress={item.onPress}
                      withoutBorder={index === customerDetail.length - 1}
                    />
                  ))}
                </View>
              </>
            ) : (
              <>
                <View style={[styles.section, styles.contentContainer]}>
                  {deliveryInfo.map((item, index) => (
                    <ItemDetail
                      key={index}
                      name={item.name}
                      value={item.value}
                      valueComponent={item.valueComponent}
                      withoutBorder={index === deliveryInfo.length - 1}
                    />
                  ))}
                </View>

                <TitleSubSection
                  title={
                    detailData?.status === 'approved'
                      ? 'PRODUCT CHECKLIST'
                      : 'PRODUCT'
                  }
                />

                {detailData?.status === 'approved' &&
                  canPerformCompletionAction && (
                    <View
                      style={[
                        styles.contentContainer,
                        {
                          backgroundColor: Colors.white,
                          justifyContent: 'space-between',
                          marginTop: 15,
                          borderBottomWidth: 1,
                          borderBottomColor: Colors.grey4,
                          paddingBottom: 25,
                          ...(isWeb
                            ? {
                                alignItems: 'center',
                                flexDirection: 'row',
                              }
                            : {}),
                        },
                      ]}>
                      <View
                        style={{
                          flex: 1,
                          ...(!isWeb
                            ? {
                                marginBottom: 20,
                              }
                            : {}),
                          ...(isNative
                            ? {
                                marginTop: 10,
                              }
                            : {}),
                        }}>
                        <View style={styles.search}>
                          <TextInput
                            value={searchKeyword}
                            placeholder={translate('Search by name...')}
                            style={styles.searchInput}
                            onChangeText={setSearchKeyword}
                          />
                          <TouchableOpacity
                            activeOpacity={0.6}
                            style={styles.searchBtn}
                            onPress={() => {}}>
                            <SociollaText
                              type="medium"
                              style={styles.searchBtnText}>
                              Search
                            </SociollaText>
                            <Image
                              style={{ width: 12, height: 12 }}
                              width={12}
                              height={12}
                              svg={true}
                              source={require('@assets/img/icons/search-white.svg')}
                            />
                          </TouchableOpacity>
                        </View>
                      </View>
                      <CheckBox
                        checked={isMarkAll}
                        checkboxStyle={{ marginBottom: 0 }}
                        checkboxContainerStyle={{ marginRight: 0 }}
                        onChange={() => {
                          const allReceived = isMarkAll;
                          const newStatus = {};
                          products.forEach((p) => {
                            newStatus[p._id || p.id] = !allReceived;
                          });
                          setProductStatus(newStatus);
                          setIsMarkAll((prev) => !prev);
                        }}>
                        <SociollaText>Mark all as received</SociollaText>
                      </CheckBox>
                    </View>
                  )}

                <View style={[styles.section, styles.contentContainer]}>
                  {products
                    .filter((item) => {
                      if (!searchKeyword) {
                        return true;
                      }
                      const name = item.name || item.mongo_product?.name || '';
                      return name
                        .toLowerCase()
                        .includes(searchKeyword.toLowerCase());
                    })
                    .map((item, index) => {
                      if (
                        detailData?.status === 'approved' &&
                        canPerformCompletionAction
                      ) {
                        return renderDeliveryProductItem(item, index);
                      }
                      if (detailData?.status === 'completed') {
                        const productItem = renderProductItem(
                          item,
                          index,
                          true,
                        );
                        const isReceived = item.is_received;
                        return (
                          <View
                            key={index}
                            style={{
                              flexDirection: 'row',
                              borderBottomWidth: 1,
                              borderBottomColor: '#F0F0F0',
                            }}>
                            <View style={{ flex: 1 }}>
                              <View
                                style={{
                                  borderBottomWidth: 0,
                                  paddingVertical: 0,
                                }}>
                                {productItem}
                              </View>
                            </View>

                            <View
                              style={{
                                width: 1,
                                backgroundColor: '#E0E0E0',
                                marginVertical: 15,
                                marginHorizontal: 15,
                              }}
                            />

                            <View
                              style={{
                                width: 140,
                                justifyContent: 'center',
                                paddingVertical: 15,
                              }}>
                              {isReceived ? (
                                <View
                                  style={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                  }}>
                                  <View
                                    style={{
                                      width: 20,
                                      height: 20,
                                      borderWidth: 1,
                                      borderColor: Colors.blue10,
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      marginRight: 8,
                                      backgroundColor: Colors.blue10,
                                    }}>
                                    <Image
                                      style={{ width: 10, height: 10 }}
                                      width={10}
                                      height={10}
                                      svg={true}
                                      source={require('@assets/img/icons/check-white.svg')}
                                    />
                                  </View>
                                  <SociollaText style={{ color: Colors.grey1 }}>
                                    Received
                                  </SociollaText>
                                </View>
                              ) : (
                                <View
                                  style={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                  }}>
                                  <View
                                    style={{
                                      width: 20,
                                      height: 20,
                                      borderWidth: 1,
                                      borderColor: Colors.red3,
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      marginRight: 8,
                                      backgroundColor: Colors.red3,
                                    }}>
                                    <Image
                                      style={{ width: 10, height: 10 }}
                                      width={10}
                                      height={10}
                                      svg={true}
                                      source={require('@assets/img/icons/close-white.svg')}
                                    />
                                  </View>
                                  <SociollaText style={{ color: Colors.grey1 }}>
                                    Not Received
                                  </SociollaText>
                                </View>
                              )}
                            </View>
                          </View>
                        );
                      }
                      if (detailData?.status === 'approved') {
                        const productItem = renderProductItem(
                          item,
                          index,
                          true,
                        );
                        const id = item._id || item.id;
                        const isReceived = productStatus[id];
                        return (
                          <View
                            key={index}
                            style={{
                              flexDirection: 'row',
                              borderBottomWidth: 1,
                              borderBottomColor: Colors.grey4,
                            }}>
                            <View style={{ flex: 1 }}>
                              <View
                                style={{
                                  borderBottomWidth: 0,
                                  paddingVertical: 0,
                                }}>
                                {productItem}
                              </View>
                            </View>

                            <View
                              style={{
                                width: 1,
                                backgroundColor: Colors.grey3,
                                marginVertical: 15,
                                marginHorizontal: 15,
                              }}
                            />

                            <View
                              style={{
                                width: 140,
                                justifyContent: 'center',
                                paddingVertical: 15,
                              }}>
                              {canPerformCompletionAction ? (
                                <>
                                  <TouchableOpacity
                                    style={{
                                      flexDirection: 'row',
                                      alignItems: 'center',
                                      marginBottom: 15,
                                    }}
                                    onPress={() =>
                                      setProductStatus({
                                        ...productStatus,
                                        [id]: true,
                                      })
                                    }
                                    activeOpacity={0.7}>
                                    <View
                                      style={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: 10,
                                        borderWidth: 1,
                                        borderColor:
                                          isReceived === true
                                            ? Colors.blue10
                                            : Colors.grey2,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: 8,
                                        backgroundColor: Colors.white,
                                      }}>
                                      {isReceived === true && (
                                        <View
                                          style={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: 5,
                                            backgroundColor: Colors.blue10,
                                          }}
                                        />
                                      )}
                                    </View>
                                    <SociollaText
                                      style={{ color: Colors.grey1 }}>
                                      Received
                                    </SociollaText>
                                  </TouchableOpacity>

                                  <TouchableOpacity
                                    style={{
                                      flexDirection: 'row',
                                      alignItems: 'center',
                                    }}
                                    onPress={() => {
                                      setProductStatus({
                                        ...productStatus,
                                        [id]: false,
                                      });
                                      setIsMarkAll(false);
                                    }}
                                    activeOpacity={0.7}>
                                    <View
                                      style={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: 10,
                                        borderWidth: 1,
                                        borderColor:
                                          isReceived === false
                                            ? Colors.blue10
                                            : Colors.grey2,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: 8,
                                        backgroundColor: Colors.white,
                                      }}>
                                      {isReceived === false && (
                                        <View
                                          style={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: 5,
                                            backgroundColor: Colors.blue10,
                                          }}
                                        />
                                      )}
                                    </View>
                                    <SociollaText
                                      style={{ color: Colors.grey1 }}>
                                      Not Received
                                    </SociollaText>
                                  </TouchableOpacity>
                                </>
                              ) : (
                                <View
                                  style={{
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}>
                                  <SociollaText
                                    style={{
                                      color: Colors.textSecondary,
                                      textAlign: 'center',
                                    }}>
                                    -
                                  </SociollaText>
                                </View>
                              )}
                            </View>
                          </View>
                        );
                      }
                      return renderProductItem(item, index);
                    })}
                  {hasMoreProducts && products.length > 0 && (
                    <View style={{ paddingVertical: 15 }}>
                      <Button
                        type="outline"
                        title={loadingMoreProducts ? 'Loading...' : 'Load More'}
                        onPress={loadMoreProducts}
                        disabled={loadingMoreProducts}
                        buttonStyle={{ width: '100%' }}
                      />
                    </View>
                  )}
                  {products.length === 0 && (
                    <View
                      style={{
                        paddingTop: 60,
                        paddingBottom: 30,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}>
                      <Image
                        style={{ width: 65, height: 65 }}
                        width={65}
                        height={65}
                        source={require('@assets/img/icons/no-product.svg')}
                        svg={true}
                      />
                      <SociollaText
                        style={{
                          marginVertical: 10,
                          fontSize: 16,
                          color: Colors.blue1,
                        }}>
                        No product found
                      </SociollaText>
                    </View>
                  )}
                </View>
              </>
            )}
          </View>
        </View>
        <RenderIf condition={!isWeb}>
          <Layout.ButtonSticky
            show={route?.name == 'RequestProductDetailScreen'}
            route={route}
            buttons={stickyButtons}
          />
        </RenderIf>
      </ContentWrapper>
      <ModalCancel
        show={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onSubmit={(reason) => handleAction('reject', reason)}
        title="Reject Request"
        description="Are you sure you want to reject this request?"
      />
      <ModalCancel
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onSubmit={(reason) => handleAction('delete', reason)}
        title="Delete Request"
        description="Are you sure you want to delete this request?"
      />
      <ModalCancel
        show={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onSubmit={(reason) => handleAction('cancel', reason)}
        title="Cancel Request"
        description="Are you sure you want to cancel this request?"
      />
      {renderModalLog()}
      {detailData?._id ? (
        <>
          {isWeb ? (
            <FloatCommentButton
              onPress={() => setShowComments(true)}
              drawerVisible={showComments}
              entityType="request_product"
              entityId={detailData._id}
            />
          ) : (
            false
          )}
          <CommentDrawer
            visible={showComments}
            onClose={() => setShowComments(false)}
            entityType="request_product"
            entityId={detailData._id}
            entityRef={detailData?.reference_code}
          />
        </>
      ) : (
        false
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  tabContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey3,
    marginBottom: 20,
    width: '100%',
  },
  tabButton: {
    paddingVertical: 15,
    paddingHorizontal: 20,
    borderBottomWidth: 2,
    borderBottomColor: Colors.transparent,
    width: isWeb ? 200 : '50%',
  },
  activeTabButton: {
    borderBottomColor: Colors.blue10,
  },
  tabText: {
    fontSize: 14,
    color: Colors.grey1,
    alignSelf: 'center',
  },
  activeTabText: {
    color: Colors.blue10,
  },
  contentContainer: {
    paddingHorizontal: 15,
  },
  section: {
    marginBottom: 20,
    backgroundColor: Colors.white,
  },
  value: {
    fontSize: 14,
    color: Colors.black,
  },
  productItem: {
    flexDirection: 'row',
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey4,
  },
  productImage: {
    width: 60,
    height: 60,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.grey4,
  },
  productName: {
    fontSize: 14,
    color: Colors.textSecondary,
    marginRight: 4,
  },
  quantity: {
    fontSize: 14,
    color: Colors.textSecondary,
    marginRight: 4,
  },
  productMeta: {
    fontSize: 13,
    color: '#7D838F',
    marginBottom: 3,
  },
  productReference: {
    fontSize: 14,
    color: Colors.textSecondary,
    marginRight: 4,
  },
  productBrand: {
    fontSize: 13,
    color: '#7D838F',
    marginTop: 4,
    marginBottom: 3,
  },
  rowCenter: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  search: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
    width: isWeb ? 300 : '100%',
  },
  searchInput: {
    width: '100%',
    height: 36,
    fontSize: 12,
    fontFamily: 'figtree-regular',
    paddingLeft: 10,
    paddingRight: 100,
    backgroundColor: '#fff',
    borderTopLeftRadius: 5,
    borderBottomLeftRadius: 5,
    borderTopRightRadius: 10,
    borderBottomRightRadius: 10,
    borderWidth: 1,
    borderColor: '#eee',
  },
  searchBtn: {
    height: 36,
    borderTopRightRadius: 10,
    borderBottomRightRadius: 10,
    backgroundColor: Colors.blue3,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 15,
    position: 'absolute',
    right: 0,
    bottom: 0,
    top: 0,
  },
  searchBtnText: {
    fontSize: 14,
    marginRight: 10,
    color: Colors.white,
  },
});

export default RequestProductDetailScreen;
